// src/helpers/rutaCategoriasProductos.js
import axios from "axios";

const RAW_BASE_URL =
  process.env.REACT_APP_API_BASE || "https://backre-miami.us-3.evennode.com";

const BASE_URL = String(RAW_BASE_URL).replace(/\/+$/, "");

const API = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

const p = (path) => `/${String(path || "").replace(/^\/+/, "")}`;

const getToken = () => localStorage.getItem("token") || "";

const auth = () => {
  const token = getToken();
  return token ? { headers: { token } } : {};
};

const debug =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase() === "true";

const logUrl = (url, params, data, method = "GET") => {
  if (!debug) return;

  console.log("👉 METHOD:", method);
  console.log("👉 URL:", API.getUri({ url, params }));

  if (data) {
    console.log("📦 DATA ENVIADA:", data);
  }
};

async function req(method, url, { params, data, signal } = {}) {
  logUrl(url, params, data, method);

  try {
    const res = await API.request({
      method,
      url,
      params,
      data,
      signal,
      ...auth(),
    });

    return res.data;
  } catch (err) {
    if (debug) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;

      console.log("🧯 API ERROR:", msg);
      console.log("🧯 STATUS:", err?.response?.status);
      console.log("🧯 DATA:", err?.response?.data);
    }

    throw err;
  }
}

/* ----------------------------- NORMALIZADORES ---------------------------- */

const clean = (v) => String(v ?? "").trim();

const unwrapCategoria = (data) => {
  if (!data) return null;
  if (data?.categoria) return data.categoria;
  if (data?.data) return data.data;
  return data;
};

const unwrapCategorias = (data) => {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.categorias)) return data.categorias;
  if (Array.isArray(data?.categoriasProductos)) return data.categoriasProductos;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.docs)) return data.docs;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;

  return [];
};

export const normalizarCategoriaProductoPayload = (payload = {}) => {
  const src = payload || {};

  const nombre = clean(src.nombre).toUpperCase();
  const codigo = clean(src.codigo || nombre)
    .toUpperCase()
    .replace(/\s+/g, "_")
    .replace(/[^A-Z0-9_]/g, "");

  return {
    ...src,
    codigo,
    nombre,
    descripcion: clean(src.descripcion).toUpperCase(),
    grupoRecepcion: clean(src.grupoRecepcion || "OTRO").toUpperCase(),
    limitePorReceptorPorVenta: Number(src.limitePorReceptorPorVenta || 0),
    usaColorDefault: src.usaColorDefault ?? false,
    activo: src.activo ?? true,
  };
};

/* -------------------------- CATEGORÍAS PRODUCTO -------------------------- */

export async function getCategoriasProductos({
  page = 1,
  limit = 50,
  q = "",
  signal,
} = {}) {
  const data = await req("GET", p("categorias-productos"), {
    params: {
      page,
      limit,
      q: String(q || "").trim(),
    },
    signal,
  });

  const categorias = unwrapCategorias(data);

  return {
    categorias,
    total: Number(data?.total ?? categorias.length ?? 0),
    page: Number(data?.page ?? page),
    limit: Number(data?.limit ?? limit),
  };
}

export async function getCategoriaProductoById(id, { signal } = {}) {
  const data = await req("GET", p(`categorias-productos/${id}`), { signal });
  return unwrapCategoria(data);
}

export async function getCategoriaProductoByCodigo(codigo, { signal } = {}) {
  const cod = String(codigo || "").trim();

  if (!cod) return null;

  const data = await req("GET", p(`categorias-productos/codigo/${cod}`), {
    signal,
  });

  return unwrapCategoria(data);
}

export async function postCategoriaProducto(payload, { signal } = {}) {
  const data = normalizarCategoriaProductoPayload(payload);

  if (debug) {
    console.log("🟢 postCategoriaProducto PAYLOAD:", data);
  }

  return req("POST", p("categorias-productos"), {
    data,
    signal,
  });
}

export async function putCategoriaProducto(id, payload, { signal } = {}) {
  const data = normalizarCategoriaProductoPayload(payload);

  if (debug) {
    console.log("🟣 putCategoriaProducto ID:", id);
    console.log("🟢 PAYLOAD:", data);
  }

  return req("PUT", p(`categorias-productos/${id}`), {
    data,
    signal,
  });
}

export async function deleteCategoriaProducto(id, { signal } = {}) {
  return req("DELETE", p(`categorias-productos/${id}`), { signal });
}

// Compatible con:
// buscarCategoriasProductos({ q: "moto", limit: 20 })
// buscarCategoriasProductos("moto", 20)
export async function buscarCategoriasProductos(arg1 = {}, arg2 = 20) {
  let q = "";
  let limit = 20;
  let signal;

  if (typeof arg1 === "string" || typeof arg1 === "number") {
    q = String(arg1 || "").trim();
    limit = Number(arg2 || 20);
  } else {
    q = String(arg1?.q || "").trim();
    limit = Number(arg1?.limit || 20);
    signal = arg1?.signal;
  }

  if (!q || q.length < 2) return [];

  const data = await req("GET", p("categorias-productos/buscar"), {
    params: { q, limit },
    signal,
  });

  return unwrapCategorias(data);
}